import {
  Dispatch as ReactDispatch,
  useState,
  useEffect,
  ChangeEvent,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { path } from 'ramda';

import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { getPlanErknmStatusByStatusTitle } from 'APIPackage/PlanListModule/common/getPlanErknmStatusByStatusTitle';
import { getIsPlanErknmStatusInProcess } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import {
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  planListExportPlans,
  planListExportDocsForKno,
  planListGetData,
  planListRemovePlans,
  planListSignPlanById,
} from 'PlanListPackage/MainModule/actions/planListActions';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { PlanListFiltersModal } from 'PlanListPackage/PlanListFiltersModule/components/PlanListFiltersModal';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { getPlanListData, getPlanListTotalCount } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { getErpPlanLabel } from 'PlanListPackage/MainModule/common/mapPlanStatus';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { SignModal } from 'CorePackage/MainModule/components/SignModal';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import {
  validateUnsignedKnmByPlanIdClear,
  validateUnsignedKnmByPlanIdPost,
} from 'KnmPackage/MainModule/actions/knmActions';
import { ValidatedUnsignedKnmByPlan } from 'PlanPackage/MainModule/components/ValidatedUnsignedKnmByPlan';
import { getSubjectInfo } from 'CorePackage/MainModule/common/getSubjectInfo';
import { IValidatedUnsignedKnmError } from 'APIPackage/KnmModule/interfaces/IValidatedUnsignedKnmByPlan';
import { dictionariesKnoOrganizationsItemGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import { planListFilterSetSwitcher } from '../actions/planListFilterActions';
import { CreatePlanModal } from './CreatePlanModal';
import styles from './PlanListPanel.module.scss';

interface IPlanListPanelProps {
  itemsCheck: TableCheckItems;
  searchInputValue: string;
  setSearchInputValue: ReactDispatch<SetStateAction<string>>;
}

interface IPlanListPanelStoreProps {
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  dataCount: number;
  data: IKnmPlan[];
  isErknm: boolean;
  isTestUser: boolean;
  settings: IAdminSetting[];
}

const PlanListPanelWithoutStore = ({
  itemsCheck,
  searchInputValue,
  setSearchInputValue,
  data,
  dataCount,
  isErknm,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isTestUser,
  settings,
}: IPlanListPanelProps & IPlanListPanelStoreProps) => {
  const isFilterOn: boolean = !!getStorageItem<boolean>(StorageNamesEnum.PLAN_FILTER_ON);

  const [isCreateTemplateModalVisible, setIsCreateTemplateModalVisible] = useState(false);
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(false);
  const [isSignModalVisible, setIsSignModalVisible] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const keysCheckedItems = Object.keys(itemsCheck).filter(id => itemsCheck[id]);

  const isAddPlanVisible = (isProsecutorOrSuperProsecutor && !isErknm) || (isErknm && isSupervisor);

  const isEnableKnoValidationSigning = useMemo(() => {
    const option = settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_KNO_VALIDATION_SIGNING);

    return option?.value === 'true';
  }, [settings]);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  const checkedItems = data.filter(item => itemsCheck[item.id]);
  const isAllTest = checkedItems.some(item => item.isTest);

  useEffect(() => {
    if (isErknm) {
      const isIncorrectStatus = checkedItems.some(
        item => !getIsPlanErknmStatusInProcess(getPlanErknmStatusByStatusTitle(item.erknmStatus)),
      );

      dispatch(dictionariesKnoOrganizationsItemGet());
      setIsDeleteButtonEnabled(checkedItems.length > 0 && !isIncorrectStatus && (isTestUser ? isAllTest : true));
    } else {
      const isIncorrectStatus = checkedItems.every(item => item.status === getErpPlanLabel(PlanStatusEnum.NEW));

      dispatch(dictionariesKnoOrganizationsItemGet());
      setIsDeleteButtonEnabled(checkedItems.length > 0 && (!isProsecutorOrSuperProsecutor || isIncorrectStatus));
    }
  }, [checkedItems, isAllTest, isErknm, isProsecutorOrSuperProsecutor, isTestUser]);

  const onAddPlanClick = useCallback(() => setIsCreateTemplateModalVisible(true), []);

  const handleCreateTemplateModalClose = useCallback(() => setIsCreateTemplateModalVisible(false), []);

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(planListFilterSetSwitcher(e.target.checked));
      dispatch(planListGetData());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => setIsFiltersModalVisible(true), []);

  const handleExportODSClick = useCallback(() => {
    dispatch(planListExportPlans(ExportFormatsEnum.ODS));
  }, [dispatch]);

  const handleExportXLSXClick = useCallback(() => {
    dispatch(planListExportPlans(ExportFormatsEnum.XLSX));
  }, [dispatch]);

  const checkedItemsNumber = Object.keys(itemsCheck).filter(key => itemsCheck[key]).length;

  const handleExportDocuments = useCallback(() => {
    if (checkedItemsNumber === 1) {
      const [planId] = Object.keys(itemsCheck).filter(key => itemsCheck[key]);

      dispatch(planListExportDocsForKno(Number(planId)));
    }
  }, [checkedItemsNumber, dispatch, itemsCheck]);

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setSearchInputValue(value);
      const isValid =
        value.length === 0 || (value.length === 10 && value.substr(0, 2) === '20' && !Number.isNaN(Number(value)));

      setIsSearchInputValid(isValid);
    },
    [setSearchInputValue],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Неверный формат строки');
    } else {
      setSearchInputError('');
    }
    dispatch(planListGetData(searchInputValue));
  }, [dispatch, isSearchInputValid, searchInputValue]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Неверный формат строки') : setSearchInputError('');
  }, [isSearchInputValid]);

  const handleCloseSignModal = useCallback(() => {
    dispatch(validateUnsignedKnmByPlanIdClear());
    setIsSignModalVisible(false);
  }, [dispatch]);

  const handleOpenSignModal = useCallback(() => {
    setIsSignModalVisible(true);
  }, [itemsCheck]);

  const handleSignPlan = useCallback(
    (certificate: string, errors: IValidatedUnsignedKnmError[] = []) => {
      const [currentPlanId] = Object.keys(itemsCheck).filter(key => itemsCheck[key]);

      dispatch(planListSignPlanById(Number(currentPlanId), certificate, searchInputValue, errors));
    },
    [dispatch, itemsCheck, searchInputValue],
  );

  /**
   * Валидация паспортов внутри плана на возможность подписания тем
   * же сертификатом, что и будет использован для плана
   */
  const handleValidationKnmsInPlan = useCallback(
    async (certificate: string) => {
      const [currentPlanId] = Object.keys(itemsCheck).filter(key => itemsCheck[key]);
      const subjectName = (await path(['SubjectName'], certificate)) as string;
      const subjectInfo = getSubjectInfo(subjectName);

      dispatch(
        validateUnsignedKnmByPlanIdPost(
          Number(currentPlanId),
          subjectInfo.fioSigner || '',
          subjectInfo.fioSignerCN || '',
          subjectInfo.org || '',
        ),
      );
    },
    [dispatch, itemsCheck, searchInputValue],
  );

  const dropdownItems: IDropdownItem[] = [
    {
      id: 'export-ods-plan',
      content: <DropdownItem onClick={handleExportODSClick}>Экспорт списка планов в ODS</DropdownItem>,
    },
    {
      id: 'export-xlsx-plan',
      content: <DropdownItem onClick={handleExportXLSXClick}>Экспорт списка планов в XLSX</DropdownItem>,
    },
  ];

  const dropdownItemsErp: IDropdownItem[] = [
    ...dropdownItems,
    {
      id: 'export-kno',
      content: (
        <DropdownItem onClick={handleExportDocuments} isDisabled={checkedItemsNumber !== 1}>
          Экспорт комплекта документов для КНО
        </DropdownItem>
      ),
    },
  ];

  const dropdownItemsErknm: IDropdownItem[] = [
    ...dropdownItems,
    {
      id: 'sign-plan',
      content: (
        <DropdownItem
          id="signButton"
          onClick={handleOpenSignModal}
          isDisabled={checkedItemsNumber !== 1 || (isTestUser ? !isAllTest : false)}
        >
          Подписать
        </DropdownItem>
      ),
    },
  ];

  const handleDeleteClick = useCallback(() => {
    const ids = keysCheckedItems.map(key => Number(key));

    dispatch(planListRemovePlans(ids));
  }, [dispatch, keysCheckedItems]);

  const deletingQuantityText = num2str(keysCheckedItems.length, ['план', 'плана', 'планов']);

  const handleDeleteModalOpen = useCallback(() => setIsConfirmModalVisible(true), []);

  const handleDeleteModalClose = useCallback(() => setIsConfirmModalVisible(false), []);

  const filterButtonAttributes = {
    ...(isFilterOn && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        {isAddPlanVisible && (
          <Button startIcon={styles.AddIcon} text="Добавить" id="addButton" color="primary" onClick={onAddPlanClick} />
        )}
        {(isErknm || isProsecutorOrSuperProsecutor) && (
          <Button
            className={styles.ButtonDelete}
            startIcon={styles.DeleteIcon}
            text="Удалить"
            id="deleteButton"
            onClick={handleDeleteModalOpen}
            disabled={!isDeleteButtonEnabled}
          />
        )}
        <SearchInput
          className={styles.SearchInput}
          value={searchInputValue}
          name="searchString"
          placeholder="Поиск планов"
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск плана КНМ по следующим критериям:
              <ul>
                <li>Номер плана КНМ</li>
              </ul>
              Расширенный поиск плана КНМ осуществляется с помощью Фильтра.
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          Планы моей прокуратуры&nbsp;
          <small className={styles.CurrentFilterNumbers}>({dataCount})</small>
        </p>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterOn}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
        <Dropdown disabled={dataCount < 1} items={isErknm ? dropdownItemsErknm : dropdownItemsErp} />
      </div>
      {isCreateTemplateModalVisible && (
        <CreatePlanModal visible={isCreateTemplateModalVisible} onClose={handleCreateTemplateModalClose} />
      )}
      {isFiltersModalVisible && <PlanListFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />}
      <SignModal
        visible={isSignModalVisible}
        title="Подписание плана"
        onClose={handleCloseSignModal}
        onSign={certificate => handleSignPlan(certificate)}
        onValidation={handleValidationKnmsInPlan}
        renderValidationResultComponent={certificate => (
          <ValidatedUnsignedKnmByPlan
            handleCloseSignModal={handleCloseSignModal}
            handleSignPlan={(errors: IValidatedUnsignedKnmError[]) => handleSignPlan(certificate, errors)}
            isFIOandJobTitle_4_1_4={isLaterThanAdminSetting(
              checkedItems?.[0]?.created,
              settings,
              AdminSettingCodesEnum.PLAN_SIGNER_166_4_1_4_START_FROM,
            )}
            isEnableKnoValidationSigning={isEnableKnoValidationSigning}
          />
        )}
      />
      <ConfirmModal
        visible={isConfirmModalVisible}
        title="Подтверждение"
        text={`Вы выбрали ${keysCheckedItems.length} ${deletingQuantityText} для удаления. Удалить?`}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteClick}
        onClose={handleDeleteModalClose}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPlanListPanelStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  dataCount: getPlanListTotalCount(state),
  data: getPlanListData(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isTestUser: getUserProfileIsTest(state),
  settings: getAdminFetchSettings(state),
});

export const PlanListPanel = connect(mapStateToProps)(PlanListPanelWithoutStore);
